import React, { useState } from 'react';
import { Loader2, ExternalLink, Copy, Check, Wand2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const SUPABASE_URL = 'https://qulnbyjrczvoxemtrili.supabase.co';

interface Annotation {
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  score: number;
  annotated_url: string;
  viewer_url: string;
}

interface AnnotateResult {
  cloudinary_url: string;
  annotated_url: string;
  viewer_url: string;
  best_match?: {
    text: string;
    score: number;
    reason?: string;
    bounds: { x: number; y: number; width: number; height: number };
  };
  annotations?: Annotation[];
}

interface AnnotateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  version: string;
  versionDate: string;
}

export const AnnotateModal: React.FC<AnnotateModalProps> = ({
  open,
  onOpenChange,
  slug,
  version,
  versionDate,
}) => {
  const [phrase, setPhrase] = useState('');
  const [multiAnnotate, setMultiAnnotate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnnotateResult | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleAnnotate = async () => {
    if (!phrase.trim()) {
      setError('Please enter a phrase to search for');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/annotate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phrase: phrase.trim(),
          slug,
          version,
          maxResults: multiAnnotate ? 5 : 3,
          multiAnnotate,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to annotate');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleAnnotate();
    }
  };

  const handleClose = () => {
    setPhrase('');
    setError(null);
    setResult(null);
    onOpenChange(false);
  };

  const isMultiResult = multiAnnotate && result?.annotations && result.annotations.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-purple-500" />
            Annotate Screenshot
          </DialogTitle>
          <DialogDescription>
            Search for text in the screenshot from {versionDate} and get an annotated highlight.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Search input */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter phrase to find (e.g., 'AI features', 'Enterprise')"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="flex-1"
            />
            <Button onClick={handleAnnotate} disabled={loading || !phrase.trim()}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                'Find'
              )}
            </Button>
          </div>

          {/* Multi-annotate toggle */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border">
            <div className="space-y-0.5">
              <Label htmlFor="multi-annotate" className="text-sm font-medium cursor-pointer">
                Multiple annotations
              </Label>
              <p className="text-xs text-gray-500">
                Highlight all matches on the full screenshot
              </p>
            </div>
            <Switch
              id="multi-annotate"
              checked={multiAnnotate}
              onCheckedChange={setMultiAnnotate}
              disabled={loading}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4 border rounded-lg p-4 bg-gray-50">
              {/* Single match mode */}
              {!isMultiResult && result.best_match && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700">Match Found</div>
                  <div className="text-sm text-gray-600">
                    <span className="font-mono bg-white px-1.5 py-0.5 rounded border">
                      {result.best_match.text}
                    </span>
                    {result.best_match.reason && (
                      <span className="ml-2 text-gray-500">— {result.best_match.reason}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Multi match mode - summary */}
              {isMultiResult && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700">
                    {result.annotations!.length} Matches Found
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {result.annotations!.map((ann, idx) => (
                      <span
                        key={idx}
                        className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border"
                      >
                        {ann.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Main links */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Links</div>
                <LinkRow
                  label="Viewer URL"
                  url={result.viewer_url}
                  copied={copiedField === 'viewer'}
                  onCopy={() => handleCopy(result.viewer_url, 'viewer')}
                />
                {!isMultiResult && (
                  <LinkRow
                    label="Annotated Image"
                    url={result.annotated_url}
                    copied={copiedField === 'annotated'}
                    onCopy={() => handleCopy(result.annotated_url, 'annotated')}
                  />
                )}
                <LinkRow
                  label="Original Screenshot"
                  url={result.cloudinary_url}
                  copied={copiedField === 'cloudinary'}
                  onCopy={() => handleCopy(result.cloudinary_url, 'cloudinary')}
                />
              </div>

              {/* Preview - only show for single annotation mode */}
              {!isMultiResult && result.annotated_url && (
                <ImagePreview
                  url={result.annotated_url}
                  label="Preview"
                />
              )}

              {/* Individual annotations for multi mode */}
              {isMultiResult && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="text-sm font-medium text-gray-700">Individual Crops</div>
                  {result.annotations!.map((annotation, idx) => (
                    <AnnotationItem
                      key={idx}
                      annotation={annotation}
                      index={idx}
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface ImagePreviewProps {
  url: string;
  label: string;
  maxHeight?: string;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ url, label, maxHeight = '300px' }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-700">{label}</div>
      {imageError ? (
        <div className="rounded-md border bg-gray-100 p-4 text-center">
          <p className="text-sm text-gray-500 mb-2">Image failed to load</p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 break-all"
          >
            Open image in new tab →
          </a>
        </div>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative"
        >
          {imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-md">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}
          <img
            src={url}
            alt={label}
            className={cn(
              "rounded-md border shadow-sm w-full object-contain bg-white",
              imageLoading && "opacity-0"
            )}
            style={{ maxHeight }}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageLoading(false);
              setImageError(true);
            }}
          />
        </a>
      )}
    </div>
  );
};

interface AnnotationItemProps {
  annotation: Annotation;
  index: number;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}

const AnnotationItem: React.FC<AnnotationItemProps> = ({
  annotation,
  index,
  copiedField,
  onCopy,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-md border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-400">#{index + 1}</span>
          <span className="font-mono text-sm bg-gray-100 px-1.5 py-0.5 rounded">
            {annotation.text}
          </span>
          <span className="text-xs text-gray-400">
            Score: {Math.round(annotation.score * 100)}%
          </span>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          {expanded ? 'Hide' : 'Show'} links
        </button>
      </div>

      {expanded && (
        <div className="space-y-1.5 pt-1">
          <LinkRow
            label="Viewer"
            url={annotation.viewer_url}
            copied={copiedField === `viewer-${index}`}
            onCopy={() => onCopy(annotation.viewer_url, `viewer-${index}`)}
          />
          <LinkRow
            label="Cropped"
            url={annotation.annotated_url}
            copied={copiedField === `annotated-${index}`}
            onCopy={() => onCopy(annotation.annotated_url, `annotated-${index}`)}
          />
          <ImagePreview
            url={annotation.annotated_url}
            label=""
            maxHeight="150px"
          />
        </div>
      )}
    </div>
  );
};

interface LinkRowProps {
  label: string;
  url: string;
  copied: boolean;
  onCopy: () => void;
}

const LinkRow: React.FC<LinkRowProps> = ({ label, url, copied, onCopy }) => {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-gray-600 shrink-0">{label}:</span>
      <div className="flex items-center gap-1 min-w-0">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 truncate max-w-[300px]"
          title={url}
        >
          {url}
        </a>
        <button
          onClick={onCopy}
          className={cn(
            "p-1 rounded hover:bg-gray-200 transition-colors shrink-0",
            copied && "text-green-600"
          )}
          title={copied ? "Copied!" : "Copy URL"}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded hover:bg-gray-200 transition-colors shrink-0"
          title="Open in new tab"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
};

export default AnnotateModal;
